/**
 * @fileoverview Where to crop each seasonal painting for Today's header, and what the
 * header shows (todayPicture).
 *
 * The paintings are square and the header is a 5:2 band, so two fifths of
 * each painting show. The Grow settings say which two fifths, as the
 * vertical part of CSS object-position, set so the gardener in each is whole,
 * head never cut off: 0% shows the top, 100% the bottom. Chosen by eye
 * against all twelve, and changed in the backend's Grow Settings panel
 * (Today tab); a painting with no setting is centred.
 *
 * They hold only for a 5:2 band. A different shape needs them set again:
 * at 16:9, early fall sat at 100%, which at 5:2 cut the gardener's head off.
 *
 * The thin strip across the top of Today is cropped by the server instead:
 * GET /today sends each painting's strip crop as painting.focus (Grow
 * setting today.STRIP_<key>), which TodayHeader uses; this file keeps only the
 * 5:2 band's.
 *
 * Where: TodayHeader, TodayPhotoDialog, TodayPictureSetting.
 * API: none.
 * Settings: Grow settings today.PAINTING_<key> (each painting's crop in the 5:2 band).
 *
 * @module paintings
 */

import { growSetting } from '@/shared/lib/growSettings';
import type { TodayDay } from '@/shared/api/today';

/** The band's shape the positions were chosen for. */
export const PAINTING_ASPECT = '5 / 2';

/**
 * A painting's crop as CSS object-position, centred when it has none of its own.
 *
 * @param key - The painting's name, e.g. fall_early.
 * @returns "center N%" from the Grow setting today.PAINTING_<key> (the server's, kept from the last visit), else "center center".
 */
export function paintingPosition(key: string): string {
  const position = key ? growSetting(`today.PAINTING_${key}`) : undefined;
  return position === undefined ? 'center center' : `center ${position}%`;
}

/**
 * What Today's picture shows: the garden's own photo when it has one that
 * loads, else the season's painting, and where it is framed.
 *
 * @param day - The server's day, or undefined while it loads.
 * @param photoFailed - Whether the garden's photo failed to load.
 * @returns The picture's address (null before the day), its object-position, and the garden's own photo's address or null.
 */
export function todayPicture(
  day: TodayDay | undefined,
  photoFailed = false,
): { src: string | null; position: string; ownPhoto: string | null } {
  const ownPhoto = day?.photo && !photoFailed ? day.photo.url : null;
  return {
    src: ownPhoto ?? day?.painting.url ?? null,
    // A photo centred, a painting where its gardener is whole.
    position: ownPhoto ? 'center center' : paintingPosition(day?.painting.key ?? ''),
    ownPhoto,
  };
}
